import { onMounted, useEventListener, watch } from '@/composition/vue/compositionApi'
import { getRouteLeaveService, getWindowService } from '@/core/container/ioc'

export const useBeforeUnload = (isDirty: () => boolean, message?: string) => {
  const onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (isDirty()) {
      event.preventDefault()
      event.returnValue = message || ''
      return event
    }

    return undefined
  }

  useEventListener(window, 'beforeunload', onBeforeUnload)
}

export const useRouteLeaveConfirmation = (isDirty: () => boolean, message: string) => {
  const routeLeaveService = getRouteLeaveService()

  onMounted(() => {
    routeLeaveService.setRouteLeaveMessage(message)
  })

  watch(isDirty, (value) => {
    routeLeaveService.setHasUnsavedChangesOnCurrentRoute(value)
  })
}

export const routeLeaveShouldBeConfirmed = (): boolean => {
  const routeLeaveService = getRouteLeaveService()
  const hasUnsavedChanges = routeLeaveService.hasUnsavedChangesOnCurrentRoute()
  const shouldSkipConfirmation = routeLeaveService.shouldSkipRouteLeaveConfirmation()
  return hasUnsavedChanges && !shouldSkipConfirmation
}

/**
 * Show a confirmation dialog when the user tries to leave the current route if there are unsaved changes.
 *
 * @return {boolean} - Returns true if the user can leave the route, false otherwise.
 */
export const confirmRouteLeaveIfNeeded = (): boolean => {
  const routeLeaveService = getRouteLeaveService()
  const windowService = getWindowService()
  const message = routeLeaveService.getRouteLeaveMessage()

  if (routeLeaveShouldBeConfirmed()) {
    const confirmationResult = windowService.self().confirm(message)
    if (confirmationResult) {
      routeLeaveService.setSkipRouteLeaveConfirmation(true)
    }

    return confirmationResult
  }

  return true
}
